import {Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {gql} from '@apollo/client';
import {Apollo} from 'apollo-angular';
import {
  TenantDetailComponent_getTenant,
  TenantDetailComponent_getTenant_tenant_anonymizedBilledClients,
  TenantDetailComponent_getTenant_tenant_coaches_edges_node,
  TenantDetailComponent_getTenantVariables,
} from './__generated__/TenantDetailComponent_getTenant';
import {catchError} from 'rxjs/operators';
import {Subscription} from 'rxjs';
import { UserRole } from 'src/app/shared/graphql/globalTypes';
import { ErrorService } from 'src/app/shared/services/error.service';

const GET_TENANT = gql`
  query TenantDetailComponent_getTenant($id: ID!) {
    me {
      id
      role
    }
    tenant(id: $id) {
      id
      coaches {
        edges {
          node {
            id
            firstName
            lastName
            email
            role
            isActive
          }
        }
      }
      anonymizedBilledClients {
        clientId
        date
      }
    }
  }
`;

interface AnonymizedBilledClientsPerMonth {
  date: string;
  clientIds: number[];
}

@Component({
  selector: 'app-tenant-detail',
  templateUrl: './tenant-detail.component.html',
  styleUrls: ['./tenant-detail.component.scss'],
})
export class TenantDetailComponent implements OnInit, OnDestroy {
  subscriptions: Subscription[] = [];
  coaches: TenantDetailComponent_getTenant_tenant_coaches_edges_node[] = [];
  anonymizedBilledClients: TenantDetailComponent_getTenant_tenant_anonymizedBilledClients[] = [];
  anonymizedBilledClientsPerMonth: AnonymizedBilledClientsPerMonth[] = [];
  showCoaches = true;
  isSuperAdmin = false;

  constructor(
    private route: ActivatedRoute,
    private apollo: Apollo,
    private router: Router,
    private errorService: ErrorService
  ) {}

  ngOnInit(): void {
    this.subscriptions.push(
      this.apollo.watchQuery<TenantDetailComponent_getTenant, TenantDetailComponent_getTenantVariables>({
        query: GET_TENANT,
        variables: {
          id: this.route.snapshot.paramMap.get('id') ?? ''
        }
      }).valueChanges.pipe(
        catchError(error => this.errorService.handleApollo(error))
      ).subscribe(output => {
        this.isSuperAdmin = output.data.me.role === UserRole.SUPERADMIN;
        if (output.data.tenant) {
          this.coaches = output.data.tenant.coaches.edges.map(edge => edge.node);
          this.anonymizedBilledClients = output.data.tenant.anonymizedBilledClients;
          this.anonymizedBilledClientsPerMonth = this.mapAnonymizedBilledClients(this.anonymizedBilledClients);
        }
      })
    );
  }

  mapAnonymizedBilledClients(
    anonymizedBilledClients: TenantDetailComponent_getTenant_tenant_anonymizedBilledClients[],
  ): AnonymizedBilledClientsPerMonth[] {
    const monthsList: AnonymizedBilledClientsPerMonth[] = [];

    const minDate: Date = new Date(
      Math.min(...anonymizedBilledClients.map(client => new Date(client.date).getTime())),
    );
    const maxDate: Date = new Date(
      Math.max(...anonymizedBilledClients.map(client => new Date(client.date).getTime())),
    );

    for (let date = new Date(minDate); date <= maxDate; date.setMonth(date.getMonth() + 1)) {
      const month: string = date.toISOString().slice(0, 7);
      const clients: number[] = [];

      monthsList.push({date: month, clientIds: clients});
    }

    for (const client of anonymizedBilledClients) {
      const month: string = client.date.slice(0, 7);
      const targetMonth = monthsList.find(m => m.date === month);

      if (targetMonth) {
        targetMonth.clientIds.push(client.clientId);
      }
    }

    return monthsList.reverse();
  }

  navigateToCoach(id: string) {
    const currentUrl = this.router.url;
    const coachDetailUrl = currentUrl + '/coach/' + id;
    this.router.navigateByUrl(coachDetailUrl);
  }

  newCoach() {
    const currentUrl = this.router.url;
    const newCoachUrl = currentUrl + '/new-coach';
    this.router.navigateByUrl(newCoachUrl);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }
}
